import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH, N1_TEXTURE_KEY } from '../config';
import { tierOfLabel, type N1Manifest, type N1TierLabel } from '../assets/n1Manifest';
import type { RegisteredAction } from '../animation/registerN1Animations';
import { publishSnapshot, pushHistory, recordError, snapshot } from '../debug/snapshot';
import { EdgeKeys, KeyboardControls } from '../input/controls';
import { buildActionGraph, type ActionGraph } from '../player/actionGraph';
import { GeorgiPlayer, type SpawnPoint } from '../player/GeorgiPlayer';
import { PLAYER_TUNING, jumpApexPx, validateTuning } from '../player/tuning';
import { DEMO_ROOM, LANDING_CLEARANCE_PX, validateRoom, type RoomData } from '../rooms/demoRoom';
import { runOptionsFromUrl } from './BootScene';
import { ROOM_TEXTURES, ensureRoomTextures } from '../world/roomTextures';

export interface RoomSceneData {
  manifest: N1Manifest;
  tierLabel: N1TierLabel;
  registered: RegisteredAction[];
}

const DEPTH = { sky: -10, pit: 4, platform: 10, sign: 12, debug: 50, hud: 100 } as const;
const MAX_DT_MS = 50;

const HELP_TEXT = [
  'A/D or ←/→ move · hold Shift to run · Space/W/↑ jump',
  'B debug overlay · H help · R respawn · G gallery',
].join('\n');

/**
 * The first playable room: generated-texture platforms with static Arcade bodies (the floating
 * ledges are one-way), a pit with a deterministic respawn, world bounds, a camera that follows
 * inside the bounds, the debug overlay (B), help (H) and the `window.__georgi` snapshot.
 */
export class RoomScene extends Phaser.Scene {
  private manifest!: N1Manifest;
  private tierLabel!: N1TierLabel;
  private registered: RegisteredAction[] = [];
  private room: RoomData = DEMO_ROOM;
  private graph!: ActionGraph;
  private player!: GeorgiPlayer;
  private controls!: KeyboardControls;
  private commands: EdgeKeys<'overlay' | 'help' | 'respawn' | 'gallery'> | null = null;
  private platforms!: Phaser.Physics.Arcade.StaticGroup;
  private sky!: Phaser.GameObjects.TileSprite;
  private helpText!: Phaser.GameObjects.Text;
  private overlayText!: Phaser.GameObjects.Text;
  private debugGraphics!: Phaser.GameObjects.Graphics;
  private overlayVisible = false;
  private helpVisible = true;
  private respawns = 0;
  private failed = false;

  constructor() {
    super('Room');
  }

  init(data: RoomSceneData): void {
    this.manifest = data.manifest;
    this.tierLabel = data.tierLabel;
    this.registered = data.registered;
    this.respawns = 0;
    this.overlayVisible = false;
    this.helpVisible = true;
    this.failed = false;
  }

  create(): void {
    const room = this.room;
    const problems = [
      ...validateTuning(PLAYER_TUNING).map((p) => `tuning: ${p}`),
      ...validateRoom(room, {
        jumpApexPx: jumpApexPx(PLAYER_TUNING),
        landingClearancePx: LANDING_CLEARANCE_PX,
      }).map((p) => `room: ${p}`),
    ];
    if (problems.length) {
      this.fail(`invalid gameplay data:\n - ${problems.join('\n - ')}`);
      return;
    }
    try {
      this.graph = buildActionGraph(
        this.manifest,
        tierOfLabel(this.tierLabel).tier,
        PLAYER_TUNING.jumpEntryLiftPx,
      );
    } catch (error) {
      this.fail(error instanceof Error ? error.message : String(error));
      return;
    }

    ensureRoomTextures(this, GAME_HEIGHT);
    this.cameras.main.setBackgroundColor('#bcdcf5');
    this.sky = this.add
      .tileSprite(0, 0, GAME_WIDTH, GAME_HEIGHT, ROOM_TEXTURES.sky)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(DEPTH.sky);

    const world = this.physics.world;
    world.setBounds(0, 0, room.width, room.height);
    // Sides contain Georgi; top and bottom stay open (the pit is the respawn trigger).
    world.setBoundsCollision(true, true, false, false);
    world.gravity.y = PLAYER_TUNING.gravity;

    this.buildRoom();

    const spawn: SpawnPoint = {
      x: room.spawn.x,
      feetY: room.spawn.feetY,
      facing: room.spawn.facing,
    };
    this.player = new GeorgiPlayer(
      this,
      this.manifest,
      this.tierLabel,
      this.graph,
      PLAYER_TUNING,
      spawn,
      { frameBlend: runOptionsFromUrl().blend },
    );
    this.physics.add.collider(this.player.sprite, this.platforms);

    const camera = this.cameras.main;
    camera.setBounds(0, 0, room.width, room.height);
    camera.startFollow(this.player.sprite, true, 0.12, 0.12);

    const keyboard = this.input.keyboard;
    if (keyboard) {
      // Tracked Key objects with replay-proof edge counters, polled once per frame — never
      // `keyboard.on('keydown-X')` (see input/edgeCounter.ts for the queue-replay pitfall).
      this.controls = new KeyboardControls(keyboard);
      const K = Phaser.Input.Keyboard.KeyCodes;
      this.commands = new EdgeKeys(keyboard, {
        overlay: K.B,
        help: K.H,
        respawn: K.R,
        gallery: K.G,
      });
    }

    this.helpText = this.add
      .text(16, 12, HELP_TEXT, {
        color: '#1d2a36',
        fontSize: '15px',
        fontFamily: 'monospace',
        backgroundColor: '#ffffffc0',
        padding: { x: 10, y: 6 },
      })
      .setScrollFactor(0)
      .setDepth(DEPTH.hud);
    this.overlayText = this.add
      .text(16, 76, '', {
        color: '#e8ffe8',
        fontSize: '13px',
        fontFamily: 'monospace',
        backgroundColor: '#0a1a10d0',
        padding: { x: 8, y: 6 },
      })
      .setScrollFactor(0)
      .setDepth(DEPTH.hud)
      .setVisible(false);
    this.debugGraphics = this.add.graphics().setDepth(DEPTH.debug);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.controls?.release());

    snapshot.phase = 'room';
    snapshot.ready = true;
    snapshot.renderer = this.game.renderer.type === Phaser.WEBGL ? 'webgl' : 'canvas';
    snapshot.registered = this.registered.length;
    snapshot.stateHistory = [];
    snapshot.actionHistory = [];
    snapshot.endless = null;
    snapshot.overlay = this.overlayVisible;
    snapshot.help = this.helpVisible;
    snapshot.room = {
      id: room.id,
      width: room.width,
      height: room.height,
      respawns: 0,
      spawn: { x: room.spawn.x, feetY: room.spawn.feetY },
      platforms: room.platforms.length,
    };
    const texture = this.textures.get(N1_TEXTURE_KEY);
    snapshot.actions = this.registered.map((r) => ({
      id: r.id,
      frames: r.frames,
      textureHas: texture.has(`${r.id}/00`),
      playing: false,
    }));
    this.publishPlayer();
    publishSnapshot();
  }

  private buildRoom(): void {
    const room = this.room;
    this.platforms = this.physics.add.staticGroup();
    for (const p of room.platforms) {
      const key =
        p.kind === 'ground'
          ? ROOM_TEXTURES.ground
          : p.kind === 'solid'
            ? ROOM_TEXTURES.solid
            : ROOM_TEXTURES.oneWay;
      this.add
        .tileSprite(p.x + p.w / 2, p.y + p.h / 2, p.w, p.h, key)
        .setDepth(DEPTH.platform)
        .setName(p.id);
      const zone = this.add.zone(p.x + p.w / 2, p.y + p.h / 2, p.w, p.h).setName(p.id);
      this.platforms.add(zone);
      const body = zone.body as Phaser.Physics.Arcade.StaticBody;
      body.updateFromGameObject();
      if (p.kind === 'one-way') {
        // Jump through from below, land on top: only the top face collides.
        body.checkCollision.down = false;
        body.checkCollision.left = false;
        body.checkCollision.right = false;
      }
    }

    // The pit reads as a hole: darkness below the ground line.
    const pitW = room.pit.right - room.pit.left;
    this.add
      .rectangle(room.pit.left, room.groundY, pitW, room.height - room.groundY, 0x2a2118, 1)
      .setOrigin(0, 0)
      .setDepth(DEPTH.pit);

    for (const s of room.signs) {
      this.add.image(s.x, room.groundY, ROOM_TEXTURES.post).setOrigin(0.5, 1).setDepth(DEPTH.sign);
      this.add
        .text(s.x, room.groundY - 46, s.text, {
          color: '#2b2418',
          fontSize: '13px',
          fontFamily: 'system-ui, sans-serif',
          backgroundColor: '#fff8e6d9',
          padding: { x: 6, y: 3 },
        })
        .setOrigin(0.5, 1)
        .setDepth(DEPTH.sign);
    }
  }

  override update(_time: number, delta: number): void {
    if (this.failed) return;
    snapshot.renderedFrames += 1;
    const commands = this.commands?.pressed();
    if (commands?.has('gallery')) {
      this.toGallery();
      return;
    }
    if (commands?.has('overlay')) this.setOverlay(!this.overlayVisible);
    if (commands?.has('help')) this.setHelp(!this.helpVisible);
    if (commands?.has('respawn')) this.respawn();
    const dt = Math.min(delta, MAX_DT_MS);
    const input = this.controls?.snapshot() ?? { moveAxis: 0, runHeld: false, jumpPressed: false };
    this.player.update(dt, input);

    if (this.player.feetY > this.room.respawnBelowY) this.respawn();

    this.sky.tilePositionX = this.cameras.main.scrollX * 0.15;
    this.publishPlayer();
    this.drawOverlay();
  }

  private respawn(): void {
    const s = this.room.spawn;
    this.respawns += 1;
    this.player.respawn({ x: s.x, feetY: s.feetY, facing: s.facing });
    const camera = this.cameras.main;
    camera.centerOn(s.x, s.feetY);
    if (snapshot.room) snapshot.room.respawns = this.respawns;
  }

  private setOverlay(on: boolean): void {
    this.overlayVisible = on;
    this.overlayText.setVisible(on);
    snapshot.overlay = on;
  }

  private setHelp(on: boolean): void {
    this.helpVisible = on;
    this.helpText.setVisible(on);
    snapshot.help = on;
  }

  private toGallery(): void {
    this.controls?.release();
    this.scene.start('Gallery', {
      manifest: this.manifest,
      tierLabel: this.tierLabel,
      registered: this.registered,
    });
  }

  private publishPlayer(): void {
    const result = this.player.result;
    const body = this.player.body;
    const machine = this.player.machine;
    const pose = result?.pose ?? machine.pose;
    const round = (v: number) => Math.round(v * 10) / 10;
    snapshot.player = {
      state: machine.state,
      facing: machine.facing,
      action: pose.action,
      frame: pose.frame,
      phase: pose.phase === null ? null : Math.round(pose.phase * 1000) / 1000,
      queue: [...pose.queue],
      x: round(this.player.x),
      feetY: round(this.player.feetY),
      vx: Math.round(body.velocity.x),
      vy: Math.round(body.velocity.y),
      grounded: this.player.grounded,
      coyoteMs: Math.round(machine.coyoteLeftMs),
      jumpBufferMs: Math.round(machine.jumpBufferLeftMs),
      body: {
        x: round(body.x),
        y: round(body.y),
        w: round(body.width),
        h: round(body.height),
      },
      bodyFamily: this.player.bodyFamily,
      blend: { next: pose.blend.nextFrame, fraction: Math.round(pose.blend.fraction * 100) / 100 },
      frameBlend: this.player.frameBlend,
    };
    snapshot.facing = machine.facing;
    pushHistory(snapshot.stateHistory, machine.state);
    pushHistory(snapshot.actionHistory, pose.action);
    const cam = this.cameras.main;
    snapshot.camera = { scrollX: round(cam.scrollX), scrollY: round(cam.scrollY) };
  }

  private drawOverlay(): void {
    const g = this.debugGraphics;
    g.clear();
    if (!this.overlayVisible) return;
    const p = snapshot.player;
    if (!p) return;
    const info = this.graph.actions[this.player.machine.pose.action];
    this.overlayText.setText(
      [
        `state    ${p.state}  facing ${p.facing}`,
        `action   ${p.action} #${p.frame}/${info.frames - 1}  phase ${p.phase === null ? 'loop' : p.phase.toFixed(2)}`,
        `queue    ${p.queue.length ? p.queue.join(' → ') : '-'}`,
        `velocity ${p.vx}, ${p.vy}   grounded ${p.grounded}   coyote ${p.coyoteMs}  buffer ${p.jumpBufferMs}`,
        `feet     ${p.x}, ${p.feetY}   body ${p.body.w}×${p.body.h} @ ${p.body.x}, ${p.body.y}`,
        `camera   ${snapshot.camera.scrollX}, ${snapshot.camera.scrollY}   respawns ${this.respawns}   fps ${Math.round(this.game.loop.actualFps)}`,
        `tier     ${this.tierLabel}   body ${p.bodyFamily}   blend ${p.frameBlend ? `${p.blend.fraction.toFixed(2)}→#${p.blend.next}` : 'off'}`,
        `yellow = body · blue = frame pixels · red = pivot/feet · green = platforms · magenta = one-way top`,
      ].join('\n'),
    );

    // Platforms (green), one-way tops (magenta).
    for (const plat of this.room.platforms) {
      g.lineStyle(2, 0x2ecc71, 0.8);
      g.strokeRect(plat.x, plat.y, plat.w, plat.h);
      if (plat.kind === 'one-way') {
        g.lineStyle(3, 0xff33ff, 1);
        g.lineBetween(plat.x, plat.y, plat.x + plat.w, plat.y);
      }
    }
    // Body (yellow) and pivot/feet cross (red).
    g.lineStyle(2, 0xffcc00, 0.95);
    g.strokeRect(p.body.x, p.body.y, p.body.w, p.body.h);
    g.lineStyle(2, 0xff3355, 1);
    g.lineBetween(p.x - 12, p.feetY, p.x + 12, p.feetY);
    g.lineBetween(p.x, p.feetY - 12, p.x, p.feetY + 12);
    // The current frame's opaque pixels (blue) — what the bodies are authored against, never the page.
    const v = this.player.visualBounds;
    g.lineStyle(1, 0x3399ff, 0.8);
    g.strokeRect(v.x, v.y, v.w, v.h);
  }

  private fail(message: string): void {
    this.failed = true;
    recordError(message);
    console.error(`[georgi] ${message}`);
    this.add
      .text(24, 24, `Georgi room failed:\n${message}`, {
        color: '#ff5555',
        fontSize: '18px',
        fontFamily: 'monospace',
        wordWrap: { width: this.scale.width - 48 },
      })
      .setOrigin(0, 0);
  }
}
